import type { FrontendDataType, FrontendFieldInfo } from '@/lib/field-types'
import { VariableHighlight } from '@/lib/tiptap-extensions/variable-highlight'
import { isTemplateToken } from '@/lib/utils/variables'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { AnimatePresence, motion } from 'framer-motion'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { allowTemplateVariables } from 'sprites-types/workflow/validation'
import { z } from 'zod'

// =============================================================================
// ZOD SCHEMA TO FRONTEND FIELD CONVERSION
// =============================================================================

/**
 * Converts a zod schema to FrontendFieldInfo, automatically detecting variable support
 */
function zodSchemaToFrontendField(
  name: string,
  schema: z.ZodTypeAny,
  data: any,
  allowVariables: boolean = true,
  fieldPath: string = name,
  originalData?: Record<string, any>
): FrontendFieldInfo {
  // Handle undefined/null data by using schema defaults if available
  let actualData = data
  if (actualData === undefined && hasDefault(schema)) {
    actualData = getDefault(schema)
  }

  // Check if current value is a template variable
  const isCurrentlyVariable = isTemplateToken(actualData)

  // Get the original schema without variable support for type detection
  const originalType = inferZodType(schema)

  // For discriminated unions, handle specially
  if (isZodDiscriminatedUnion(schema)) {
    const unionInfo = extractDiscriminatedUnionInfo(schema, actualData)

    const field: FrontendFieldInfo = {
      name,
      type: 'discriminated-union' as const,
      value: actualData,
      isOptional: schema.isOptional(),
      isArray: false,
      discriminatorKey: unionInfo.discriminatorKey,
      discriminatorValue: actualData?.[unionInfo.discriminatorKey],
      discriminatorOptions: unionInfo.options,
      unionVariant: unionInfo.currentVariant
        ? {
            fields: Object.entries(unionInfo.currentVariant.shape)
              .filter(([key]) => key !== unionInfo.discriminatorKey) // Filter out discriminator field from variant fields
              .map(([key, fieldSchema]) => {
                const variantField = zodSchemaToFrontendField(
                  key,
                  fieldSchema as z.ZodTypeAny,
                  actualData?.[key],
                  allowVariables,
                  `${fieldPath}.${key}`,
                  originalData
                )
                return variantField
              }),
          }
        : undefined,
    }

    return field
  }

  // Helper to detect array element schema even when wrapped by unions/defaults/optionals
  const findArrayElementSchema = (s: z.ZodTypeAny): z.ZodTypeAny | null => {
    const getTypeName = (x: any): string => x?._def?.typeName || x?.constructor?.name
    let current: any = s
    let typeName = getTypeName(current)

    while (typeName === 'ZodOptional' || typeName === 'ZodNullable' || typeName === 'ZodDefault') {
      current = current?._def?.innerType
      typeName = getTypeName(current)
    }

    if (typeName === 'ZodArray') {
      return current?.element || current?._def?.type || null
    }

    if (typeName === 'ZodUnion') {
      const options = current?._def?.options || []
      for (const option of options) {
        const inner = findArrayElementSchema(option)
        if (inner) return inner
      }
    }

    return null
  }

  // For arrays (including schemas wrapped by allowTemplateVariables unions)
  if (inferZodType(schema) === 'array' || isZodArray(schema)) {
    const elementSchema = (findArrayElementSchema(schema) ||
      (schema as any)?.element) as z.ZodTypeAny
    const arrayData = Array.isArray(actualData) ? actualData : []

    // For arrays of enums, we need to extract and preserve enum options for array items
    // This handles cases like z.array(z.enum(['option1', 'option2']))
    const arrayElementType = inferZodType(elementSchema)
    let enumValues: string[] | undefined
    if (arrayElementType === 'enum') {
      enumValues = extractEnumOptions(elementSchema)
    }

    // Only treat as pure template variable if the value is exactly a template string
    const isPureTemplateVariable = isCurrentlyVariable && typeof actualData === 'string'

    const arrayField: FrontendFieldInfo = {
      name,
      type: originalType,
      value: isPureTemplateVariable ? actualData : arrayData,
      isOptional: schema.isOptional(),
      isArray: true,
      arrayElementType,
      enumValues, // Add enum values for array elements
      properties: isPureTemplateVariable
        ? undefined
        : arrayData.map((item, index) => {
            const itemField = zodSchemaToFrontendField(
              `${index}`,
              elementSchema,
              item,
              allowVariables,
              `${fieldPath}[${index}]`,
              originalData
            )
            // Ensure enum values are preserved for array items
            if (arrayElementType === 'enum' && enumValues) {
              itemField.enumValues = enumValues
            }
            return itemField
          }),
    }

    // Set modification status if original data is provided
    if (originalData !== undefined) {
      const originalValue = getNestedValue(originalData, fieldPath)
      const currentValue = isPureTemplateVariable ? actualData : arrayData

      arrayField.isModified = !deepEqual(currentValue, originalValue)
    }

    return arrayField
  }

  // For objects
  if (isZodObject(schema)) {
    const shape = (schema as z.ZodObject<any>).shape

    // Only treat as pure template variable if the value is exactly a template string
    // Otherwise, allow destructuring even if the object contains template variables
    const isPureTemplateVariable = isCurrentlyVariable && typeof actualData === 'string'

    const objectField: FrontendFieldInfo = {
      name,
      type: 'object',
      value: isPureTemplateVariable ? actualData : actualData || {},
      isOptional: schema.isOptional(),
      isArray: false,
      properties: isPureTemplateVariable
        ? undefined
        : Object.entries(shape).map(([key, fieldSchema]) => {
            const nestedFieldPath = `${fieldPath}.${key}`
            const nestedData = actualData?.[key]

            return zodSchemaToFrontendField(
              key,
              fieldSchema as z.ZodTypeAny,
              nestedData,
              allowVariables,
              nestedFieldPath,
              originalData
            )
          }),
    }

    // Set modification status if original data is provided
    if (originalData !== undefined) {
      const originalValue = getNestedValue(originalData, fieldPath)
      const currentValue = isPureTemplateVariable ? actualData : actualData || {}

      objectField.isModified = !deepEqual(currentValue, originalValue)
    }

    return objectField
  }

  // For enums
  if (isZodEnum(schema)) {
    const options = extractEnumOptions(schema)

    // Validate that the current value is a valid enum option
    if (
      actualData !== undefined &&
      actualData !== null &&
      !isCurrentlyVariable &&
      options.length > 0 &&
      !options.includes(actualData)
    ) {
      // If the value is not in the enum options, log a warning but preserve the value
      // This allows for better UX when switching between edit modes
      console.warn(
        `[NodeDataDisplay] ⚠️  Value "${actualData}" not in options [${options.join(', ')}], but preserving value for edit mode`
      )
      // Don't reset to undefined - preserve the value for edit mode
    } else {
      console.log(
        `[NodeDataDisplay] ✓ Value "${actualData}" is valid in options [${options.join(', ')}]`
      )
    }

    const enumField: FrontendFieldInfo = {
      name,
      type: 'enum' as const,
      value: actualData,
      isOptional: schema.isOptional(),
      isArray: false,
      enumValues: options,
    }

    // Set modification status if original data is provided
    if (originalData !== undefined) {
      const originalValue = getNestedValue(originalData, fieldPath)
      const currentValue = actualData

      enumField.isModified = !deepEqual(currentValue, originalValue)
    }

    return enumField
  }

  const primitiveField: FrontendFieldInfo = {
    name,
    type: originalType,
    value: actualData,
    isOptional: schema.isOptional(),
    isArray: false,
  }

  // Set modification status if original data is provided
  if (originalData !== undefined) {
    const originalValue = getNestedValue(originalData, fieldPath)
    const currentValue = actualData

    primitiveField.isModified = !deepEqual(currentValue, originalValue)
  }

  return primitiveField
}

/**
 * Infer FrontendDataType from zod schema
 */
function inferZodType(schema: z.ZodTypeAny): FrontendDataType {
  // Use the same approach as OutputSchemaDisplay
  const getTypeName = (s: any): string => s._def?.typeName || s.constructor.name

  // Unwrap optional/nullable/default using typeName approach
  let unwrapped = schema
  let typeName = getTypeName(unwrapped)

  while (typeName === 'ZodOptional' || typeName === 'ZodNullable' || typeName === 'ZodDefault') {
    unwrapped = (unwrapped._def as any).innerType
    typeName = getTypeName(unwrapped)
  }

  // Handle unions (like those created by allowTemplateVariables)
  if (typeName === 'ZodUnion') {
    const options = (unwrapped._def as any).options

    // Find the original type option (not the template variable string)
    for (const option of options || []) {
      const optionTypeName = getTypeName(option)

      // Skip template variable strings (they have regex patterns)
      if (
        optionTypeName === 'ZodString' &&
        option._def.checks?.some(
          (check: any) => check.kind === 'regex' && check.regex.source.includes('{{')
        )
      ) {
        continue
      }

      // Return the type of the first non-template-variable option
      const inferredType = inferZodType(option)
      return inferredType
    }

    // Fallback to first option if no non-template option found
    if (options && options.length > 0) {
      const fallbackType = inferZodType(options[0])
      return fallbackType
    }
  }

  // Handle primitive types using typeName
  switch (typeName) {
    case 'ZodString':
      return 'string'
    case 'ZodNumber':
      return 'number'
    case 'ZodBoolean':
      return 'boolean'
    case 'ZodArray':
      return 'array'
    case 'ZodObject':
      return 'object'
    case 'ZodEnum':
      return 'enum'
    case 'ZodNativeEnum':
      return 'enum'
    case 'ZodLiteral':
      return 'enum'
    case 'ZodDate':
      return 'date'
    case 'ZodDiscriminatedUnion':
      return 'discriminated-union'
    default:
      return 'unknown'
  }
}

/**
 * Type guards for zod types
 */
function isZodArray(schema: z.ZodTypeAny): boolean {
  // Use internal typeName for cross-package compatibility (fixes Vercel issue)
  const typeName = (schema as any)?._def?.typeName
  return (
    typeName === 'ZodArray' ||
    schema instanceof z.ZodArray ||
    (schema.constructor.name === 'ZodArray' && schema._def && schema._def.type)
  )
}

function isZodObject(schema: z.ZodTypeAny): boolean {
  // Use internal typeName for cross-package compatibility (fixes Vercel issue)
  const typeName = (schema as any)?._def?.typeName
  const result =
    typeName === 'ZodObject' ||
    schema instanceof z.ZodObject ||
    (schema.constructor.name === 'ZodObject' &&
      schema._def &&
      typeof schema._def.shape === 'function')

  return result
}

function isZodEnum(schema: z.ZodTypeAny): boolean {
  // Use the same approach as OutputSchemaDisplay
  const getTypeName = (s: any): string => s._def?.typeName || s.constructor.name

  // Unwrap optional/nullable/default using typeName approach
  let unwrapped = schema
  let typeName = getTypeName(unwrapped)

  while (typeName === 'ZodOptional' || typeName === 'ZodNullable' || typeName === 'ZodDefault') {
    unwrapped = (unwrapped._def as any).innerType
    typeName = getTypeName(unwrapped)
  }

  // Handle unions (created by allowTemplateVariables)
  if (typeName === 'ZodUnion') {
    const options = (unwrapped._def as any).options

    const hasEnum = options?.some((option: any) => {
      const optionTypeName = getTypeName(option)

      // Skip template variable strings
      if (
        optionTypeName === 'ZodString' &&
        option._def.checks?.some(
          (check: any) => check.kind === 'regex' && check.regex.source.includes('{{')
        )
      ) {
        return false
      }

      // Check for direct enum types
      if (
        optionTypeName === 'ZodEnum' ||
        optionTypeName === 'ZodNativeEnum' ||
        optionTypeName === 'ZodLiteral'
      ) {
        return true
      }

      // Check if ZodDefault wraps an enum
      if (optionTypeName === 'ZodDefault') {
        const innerType = option._def?.innerType
        if (innerType) {
          const innerTypeName = getTypeName(innerType)
          return (
            innerTypeName === 'ZodEnum' ||
            innerTypeName === 'ZodNativeEnum' ||
            innerTypeName === 'ZodLiteral'
          )
        }
      }

      return false
    })

    return hasEnum || false
  }

  const isDirectEnum =
    typeName === 'ZodEnum' || typeName === 'ZodNativeEnum' || typeName === 'ZodLiteral'

  return isDirectEnum
}

function isZodDiscriminatedUnion(schema: z.ZodTypeAny): boolean {
  // Use internal typeName for cross-package compatibility (fixes Vercel issue)
  const typeName = (schema as any)?._def?.typeName
  return typeName === 'ZodDiscriminatedUnion'
}

function extractDiscriminatedUnionInfo(
  schema: z.ZodTypeAny,
  data?: any
): {
  discriminatorKey: string
  options: string[]
  currentVariant?: z.ZodObject<any>
} {
  if (!isZodDiscriminatedUnion(schema)) {
    throw new Error('Schema is not a discriminated union')
  }

  const def = (schema as any)._def
  const discriminatorKey: string = def.discriminator
  const rawOptions = def.options

  // Handle different option formats
  const optionsArray = Array.isArray(rawOptions)
    ? rawOptions
    : typeof rawOptions?.values === 'function'
      ? Array.from(rawOptions.values())
      : []

  // Extract discriminator values from each option and find current variant
  const options: string[] = []
  let currentVariant: z.ZodObject<any> | undefined = undefined
  const currentDiscriminatorValue = data?.[discriminatorKey]

  for (const option of optionsArray) {
    if (option && typeof option === 'object' && option.shape) {
      const discriminatorField = option.shape[discriminatorKey]
      if (discriminatorField && discriminatorField._def?.value !== undefined) {
        const discriminatorValue = String(discriminatorField._def.value)
        options.push(discriminatorValue)

        // Check if this is the current variant based on the data
        if (currentDiscriminatorValue === discriminatorValue) {
          currentVariant = option
        }
      }
    }
  }

  return {
    discriminatorKey,
    options,
    currentVariant,
  }
}

function extractEnumOptions(schema: z.ZodTypeAny): string[] {
  // Use the same approach as OutputSchemaDisplay
  const getTypeName = (s: any): string => s._def?.typeName || s.constructor.name

  // Unwrap optional/nullable/default using typeName approach
  let unwrapped = schema
  let typeName = getTypeName(unwrapped)

  while (typeName === 'ZodOptional' || typeName === 'ZodNullable' || typeName === 'ZodDefault') {
    unwrapped = (unwrapped._def as any).innerType
    typeName = getTypeName(unwrapped)
  }

  // Handle unions (created by allowTemplateVariables)
  if (typeName === 'ZodUnion') {
    const options = (unwrapped._def as any).options

    // Find the enum/literal option (not the string template variable option)
    for (const option of options || []) {
      const optionTypeName = getTypeName(option)

      if (optionTypeName === 'ZodEnum') {
        const enumOptions = (option._def as any).values
        return enumOptions
      }

      if (optionTypeName === 'ZodNativeEnum') {
        const nativeEnumOptions = Object.values((option._def as any).values) as string[]
        return nativeEnumOptions
      }

      if (optionTypeName === 'ZodLiteral') {
        const literalOptions = [String((option._def as any).value)]
        return literalOptions
      }

      // Check if ZodDefault wraps an enum
      if (optionTypeName === 'ZodDefault') {
        const innerType = option._def?.innerType
        if (innerType) {
          const innerTypeName = getTypeName(innerType)

          if (innerTypeName === 'ZodEnum') {
            const enumOptions = (innerType._def as any).values
            return enumOptions
          }

          if (innerTypeName === 'ZodNativeEnum') {
            const nativeEnumOptions = Object.values((innerType._def as any).values) as string[]
            return nativeEnumOptions
          }

          if (innerTypeName === 'ZodLiteral') {
            const literalOptions = [String((innerType._def as any).value)]
            return literalOptions
          }
        }
      }
    }
  }

  // Handle direct enum types
  if (typeName === 'ZodEnum') {
    const directEnumOptions = (unwrapped._def as any).values
    return directEnumOptions
  }

  if (typeName === 'ZodNativeEnum') {
    const directNativeEnumOptions = Object.values((unwrapped._def as any).values) as string[]
    return directNativeEnumOptions
  }

  if (typeName === 'ZodLiteral') {
    const directLiteralOptions = [String((unwrapped._def as any).value)]
    return directLiteralOptions
  }

  return []
}

/**
 * Create FrontendFieldInfo array from zod schema and data
 */
export function createFieldsFromSchema(
  schema: z.ZodTypeAny,
  data: Record<string, any> = {},
  allowVariables: boolean = true,
  originalData?: Record<string, any>,
  fieldPath: string = ''
): FrontendFieldInfo[] {
  // Apply allowTemplateVariables if requested
  const enhancedSchema = allowVariables ? allowTemplateVariables(schema) : schema

  // Handle object schemas
  if (isZodObject(enhancedSchema)) {
    const shape = (enhancedSchema as z.ZodObject<any>).shape
    const fields = Object.entries(shape).map(([key, fieldSchema]) => {
      const currentFieldPath = fieldPath ? `${fieldPath}.${key}` : key
      const field = zodSchemaToFrontendField(
        key,
        fieldSchema as z.ZodTypeAny,
        data[key],
        allowVariables,
        currentFieldPath,
        originalData
      )

      // Determine if field is modified by comparing with original data
      if (originalData !== undefined) {
        field.isModified = !deepEqual(data[key], originalData[key])
      }

      return field
    })

    return fields
  }

  // Handle case where allowTemplateVariables incorrectly wraps object schema in union
  const isUnion =
    (enhancedSchema as any)?._def?.typeName === 'ZodUnion' || enhancedSchema instanceof z.ZodUnion
  if (isUnion) {
    const options = (enhancedSchema as any).options

    // Look for an object schema in the union options
    const objectOption = options.find((option: z.ZodTypeAny) => isZodObject(option))

    if (objectOption) {
      const shape = (objectOption as z.ZodObject<any>).shape
      const fields = Object.entries(shape).map(([key, fieldSchema]) => {
        const currentFieldPath = fieldPath ? `${fieldPath}.${key}` : key
        const field = zodSchemaToFrontendField(
          key,
          fieldSchema as z.ZodTypeAny,
          data[key],
          allowVariables,
          currentFieldPath,
          originalData
        )

        // Determine if field is modified by comparing with original data
        if (originalData !== undefined) {
          field.isModified = !deepEqual(data[key], originalData[key])
        }

        return field
      })

      return fields
    }
  }

  // For non-object schemas, return single field

  const field = zodSchemaToFrontendField(
    'value',
    enhancedSchema,
    data,
    allowVariables,
    fieldPath || 'value',
    originalData
  )
  if (originalData !== undefined) {
    field.isModified = !deepEqual(data, originalData)
  }

  return [field]
}

/**
 * Deep equality check for comparing field values
 */
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true
  if (a == null || b == null) return a === b
  if (typeof a !== typeof b) return false

  // Special handling for strings - normalize them for comparison
  if (typeof a === 'string') {
    // Normalize strings by trimming whitespace, normalizing unicode, and normalizing whitespace
    const normalizedA = a.trim().normalize().replace(/\s+/g, ' ')
    const normalizedB = b.trim().normalize().replace(/\s+/g, ' ')

    if (normalizedA === normalizedB) {
      console.log('[deepEqual] ✓ Strings are identical after normalization:', {
        originalA: a,
        originalB: b,
        normalizedA,
        normalizedB,
      })
      return true
    }

    // If they're still different, log detailed information
    console.log('[deepEqual] ✗ String comparison failed:', {
      originalA: a,
      originalB: b,
      normalizedA,
      normalizedB,
      aLength: a.length,
      bLength: b.length,
      normalizedALength: normalizedA.length,
      normalizedBLength: normalizedB.length,
    })
    return false
  }

  if (typeof a !== 'object') return a === b

  if (Array.isArray(a) !== Array.isArray(b)) return false
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => deepEqual(item, b[index]))
  }

  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false

  return keysA.every((key) => deepEqual(a[key], b[key]))
}

/**
 * Navigate nested object using field path (handles both dot notation and array indices)
 */
function getNestedValue(obj: any, path: string): any {
  if (!path || path === '') return obj

  // Split on dots but preserve array notation
  const parts = path.split('.')
  let current = obj

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined
    }

    // Handle array indices like "userPrompts[0]"
    const arrayMatch = part.match(/^(.+)\[(\d+)\]$/)
    if (arrayMatch) {
      const [, arrayName, indexStr] = arrayMatch
      const index = parseInt(indexStr, 10)

      if (
        current[arrayName] &&
        Array.isArray(current[arrayName]) &&
        index < current[arrayName].length
      ) {
        current = current[arrayName][index]
      } else {
        return undefined
      }
    } else {
      // Regular property access
      current = current[part]
    }
  }

  return current
}

/**
 * Check if a schema has a default value
 */
function hasDefault(schema: z.ZodTypeAny): boolean {
  // Check direct default
  if ('_def' in schema && 'defaultValue' in (schema as any)._def) {
    return true
  }

  // Check for ZodDefault within union options
  const isUnionSchema =
    (schema as any)?._def?.typeName === 'ZodUnion' || schema instanceof z.ZodUnion
  if (isUnionSchema) {
    const options = (schema._def as any).options
    return (
      options?.some((option: z.ZodTypeAny) => {
        const getTypeName = (s: any): string => s._def?.typeName || s.constructor.name
        return getTypeName(option) === 'ZodDefault'
      }) || false
    )
  }

  return false
}

/**
 * Get the default value from a schema
 */
function getDefault(schema: z.ZodTypeAny): any {
  // Check direct default first
  if ('_def' in schema && 'defaultValue' in (schema as any)._def) {
    const defaultValue = (schema as any)._def.defaultValue
    return typeof defaultValue === 'function' ? defaultValue() : defaultValue
  }

  // Check for ZodDefault within union options
  const isUnionSchema =
    (schema as any)?._def?.typeName === 'ZodUnion' || schema instanceof z.ZodUnion
  if (isUnionSchema) {
    const options = (schema._def as any).options
    for (const option of options || []) {
      const getTypeName = (s: any): string => s._def?.typeName || s.constructor.name
      if (getTypeName(option) === 'ZodDefault') {
        const defaultValue = (option._def as any).defaultValue
        return typeof defaultValue === 'function' ? defaultValue() : defaultValue
      }
    }
  }

  return undefined
}

// =============================================================================
// SHARED COMPONENTS
// =============================================================================

const ChevronIcon: React.FC<{ isExpanded: boolean; onClick: (e: React.MouseEvent) => void }> = ({
  isExpanded,
  onClick,
}) => (
  <button
    onClick={onClick}
    className="rounded p-0.5 transition-colors duration-150 hover:bg-gray-200"
    aria-label={isExpanded ? 'Collapse' : 'Expand'}
  >
    <svg
      className={`h-4 w-4 text-gray-500 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </button>
)

// =============================================================================
// TYPE BADGES
// =============================================================================

const getTypeColor = (type: FrontendDataType): string => {
  switch (type) {
    case 'string':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'number':
      return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'boolean':
      return 'bg-purple-100 text-purple-800 border-purple-200'
    case 'date':
      return 'bg-orange-100 text-orange-800 border-orange-200'
    case 'object':
      return 'bg-gray-100 text-gray-800 border-gray-200'
    case 'enum':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'discriminated-union':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200'
    default:
      return 'bg-slate-100 text-slate-800 border-slate-200'
  }
}

const Badge: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <span className={`rounded border px-2 py-1 text-xs font-medium ${className}`}>{children}</span>
)

interface TypeBadgesProps {
  type: FrontendDataType
  isArray?: boolean
  isOptional?: boolean
  isModified?: boolean
}

const TypeBadges: React.FC<TypeBadgesProps> = ({ type, isArray, isOptional, isModified }) => (
  <div className="flex flex-wrap items-center gap-1">
    {isArray ? (
      <Badge className="border-red-200 bg-red-100 text-red-800">array</Badge>
    ) : (
      <Badge className={getTypeColor(type)}>{type}</Badge>
    )}
    {isOptional && (
      <Badge className="border-orange-200 bg-orange-100 text-orange-700">optional</Badge>
    )}
    {isModified && <Badge className="border-blue-200 bg-blue-100 text-blue-700">modified</Badge>}
  </div>
)

// =============================================================================
// VALUE DISPLAY
// =============================================================================

interface ExpandableValueProps {
  value: any
  maxLength?: number
}

const ExpandableValue: React.FC<ExpandableValueProps> = ({ value, maxLength = 200 }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  // If this is a template token, display it as a variable tag
  if (isTemplateToken(value)) {
    return (
      <div className="mt-1">
        <VariableTag variable={value} />
      </div>
    )
  }

  const stringValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
  const isTruncated = stringValue.length > maxLength && !isExpanded
  const displayValue = isTruncated ? stringValue.substring(0, maxLength) + '...' : stringValue

  // Function to render text with highlighted variables
  const renderWithHighlights = (text: string) => {
    // Check if text contains variables
    if (typeof text === 'string' && text.includes('{{')) {
      const parts = text.split(/(\{\{[^}]+\}\})/g)
      return parts.map((part: string, index: number) => {
        if (part.match(/^\{\{[^}]+\}\}$/)) {
          return <VariableTag key={index} variable={part} />
        }
        return <span key={index}>{part}</span>
      })
    }
    return text
  }

  if (stringValue.length <= maxLength) {
    return (
      <div className="mt-1 whitespace-pre-wrap break-words rounded border border-gray-200 bg-white p-2 text-sm text-gray-800">
        {renderWithHighlights(displayValue)}
      </div>
    )
  }

  return (
    <div className="mt-1">
      <div className="whitespace-pre-wrap break-words rounded border border-gray-200 bg-white p-2 text-sm text-gray-800">
        {renderWithHighlights(displayValue)}
      </div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mt-1 text-xs text-blue-600 underline hover:text-blue-800"
      >
        {isExpanded ? 'Show less' : 'Show more'}
      </button>
    </div>
  )
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Helper function to get appropriate default values when switching from variable to literal
const getDefaultValueForType = (fieldType: FrontendDataType): any => {
  switch (fieldType) {
    case 'string':
      return ''
    case 'number':
      return 0
    case 'boolean':
      return false
    case 'date':
      return ''
    case 'object':
      return {}
    case 'array':
      return []
    case 'enum':
      return ''
    default:
      return undefined
  }
}

// =============================================================================
// INPUT COMPONENTS
// =============================================================================

interface BaseInputProps {
  value: any
  onChange: (value: any) => void
  className?: string
}

// Variable Tag Component for displaying variables nicely
const VariableTag: React.FC<{ variable: string }> = ({ variable }) => (
  <span
    className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium"
    style={{
      backgroundColor: 'rgba(200, 127, 255, 0.1)',
      color: '#C87FFF',
      border: '1px solid rgba(200, 127, 255, 0.2)',
    }}
  >
    {variable}
  </span>
)

const VariableTokenInput: React.FC<BaseInputProps> = ({ value, onChange, className = '' }) => {
  // Extract just the variable name from {{variable}} format
  const getVariableName = (templateValue: string): string => {
    if (typeof templateValue !== 'string') return ''
    const match = templateValue.match(/^\{\{(.+)\}\}$/)
    return match ? match[1].trim() : ''
  }

  // Convert variable name back to {{variable}} format
  const setVariableName = (variableName: string) => {
    // Don't trim - preserve spaces at the end
    onChange(variableName ? `{{${variableName}}}` : '{{variable}}')
  }

  const variableName = getVariableName(value || '')

  return (
    <div
      className={`mt-1 flex items-center rounded border ${className}`}
      style={{
        borderColor: 'rgba(200, 127, 255, 0.3)',
        backgroundColor: 'rgba(200, 127, 255, 0.05)',
      }}
    >
      <span className="pl-3 font-mono text-sm" style={{ color: '#C87FFF' }}>
        {'{'}
      </span>
      <span className="font-mono text-sm" style={{ color: '#C87FFF' }}>
        {'{'}
      </span>
      <textarea
        className="flex-1 resize-none border-0 bg-transparent px-1 py-2 text-sm focus:outline-none focus:ring-0"
        rows={1}
        placeholder="variable"
        value={variableName}
        onChange={(e) => setVariableName(e.target.value)}
        style={{ color: '#C87FFF' }}
      />
      <span className="font-mono text-sm" style={{ color: '#C87FFF' }}>
        {'}'}
      </span>
      <span className="pr-3 font-mono text-sm" style={{ color: '#C87FFF' }}>
        {'}'}
      </span>
    </div>
  )
}

const BooleanInput: React.FC<BaseInputProps> = ({ value, onChange, className = '' }) => {
  if (isTemplateToken(value)) {
    return <VariableTokenInput value={value} onChange={onChange} className={className} />
  }
  return (
    <select
      className={`mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm ${className}`}
      value={value === null ? 'null' : String(value)}
      onChange={(e) => {
        if (e.target.value === 'null') {
          onChange(null)
        } else {
          onChange(e.target.value === 'true')
        }
      }}
    >
      <option value="null">null</option>
      <option value="true">true</option>
      <option value="false">false</option>
    </select>
  )
}

const NumberInput: React.FC<BaseInputProps> = ({ value, onChange, className = '' }) => {
  if (isTemplateToken(value)) {
    return <VariableTokenInput value={value} onChange={onChange} className={className} />
  }
  return (
    <input
      type="number"
      className={`mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm ${className}`}
      value={value === null ? '' : (value ?? '')}
      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
    />
  )
}

const DateInput: React.FC<BaseInputProps> = ({ value, onChange, className = '' }) => {
  if (isTemplateToken(value)) {
    return <VariableTokenInput value={value} onChange={onChange} className={className} />
  }
  return (
    <input
      type="datetime-local"
      className={`mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm ${className}`}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

const TextInput: React.FC<BaseInputProps> = ({ value, onChange, className = '' }) => {
  // Track if the user is actively typing to prevent cursor jumping
  const isUserTyping = useRef(false)
  const lastUserInput = useRef('')

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        horizontalRule: false,
        listItem: false,
        orderedList: false,
        bulletList: false,
        codeBlock: false,
      }),
      VariableHighlight,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const text = editor.getText()
      lastUserInput.current = text
      isUserTyping.current = true
      onChange(text)

      // Apply highlighting immediately after every keystroke
      // Use a very short delay to ensure the content is updated
      setTimeout(() => highlightVariables(editor), 10)

      // Reset typing flag after a longer delay to prevent interference
      setTimeout(() => {
        isUserTyping.current = false
      }, 200)
    },
    onCreate: ({ editor }) => {
      // Apply highlighting immediately when editor is created
      setTimeout(() => highlightVariables(editor), 50)
    },
  })

  // Function to highlight variables in the editor with better regex
  const highlightVariables = useCallback((editorInstance: any) => {
    if (!editorInstance) return

    try {
      const content = editorInstance.getHTML()

      // First, remove any existing variable highlighting to prevent accumulation
      const cleanContent = content.replace(/<span data-variable[^>]*>(.*?)<\/span>/g, '$1')

      // Then apply new highlighting with precise regex
      // This regex matches complete {{variable}} patterns and handles edge cases
      const highlightedContent = cleanContent.replace(
        /\{\{([^}]*?)\}\}/g,
        '<span data-variable style="color: #C87FFF; background-color: rgba(200, 127, 255, 0.1); padding: 1px 2px; border-radius: 2px;">{{$1}}</span>'
      )

      if (highlightedContent !== content) {
        // Store cursor position before updating
        const { from, to } = editorInstance.state.selection

        editorInstance.commands.setContent(highlightedContent)

        // Restore cursor position after highlighting
        const newLength = editorInstance.state.doc.content.size
        const newFrom = Math.min(from, newLength)
        const newTo = Math.min(to, newLength)
        editorInstance.commands.setTextSelection({ from: newFrom, to: newTo })
      }
    } catch (error) {
      console.warn('Error during variable highlighting:', error)
    }
  }, [])

  // Update editor content when value changes externally
  useEffect(() => {
    if (editor && !isUserTyping.current && editor.getText() !== (value || '')) {
      // Only update if the user is not actively typing
      editor.commands.setContent(value || '')
      setTimeout(() => highlightVariables(editor), 100)
    }
  }, [editor, value, highlightVariables])

  // Initial highlighting when editor first loads with content
  useEffect(() => {
    if (editor && value) {
      setTimeout(() => highlightVariables(editor), 150)
    }
  }, [editor, highlightVariables, value])

  // Cleanup editor on unmount
  useEffect(() => {
    return () => {
      editor?.destroy()
    }
  }, [editor])

  return (
    <div className={`mt-1 ${className}`}>
      <EditorContent
        editor={editor}
        className="min-h-[200px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 [&_.ProseMirror]:min-h-[176px] [&_.ProseMirror]:whitespace-pre-wrap [&_.ProseMirror]:outline-none"
      />
    </div>
  )
}

interface PrimitiveInputProps {
  type: FrontendDataType
  value: any
  onChange: (value: any) => void
}

const PrimitiveInput: React.FC<PrimitiveInputProps> = ({ type, value, onChange }) => {
  const inputProps = { value, onChange }

  switch (type) {
    case 'boolean':
      return <BooleanInput {...inputProps} />
    case 'number':
      return <NumberInput {...inputProps} />
    case 'date':
      return <DateInput {...inputProps} />
    case 'string':
    default:
      return <TextInput {...inputProps} />
  }
}

// =============================================================================
// FIELD CONTAINER COMPONENTS
// =============================================================================

interface FieldHeaderProps {
  name: string
  type: FrontendDataType
  isArray?: boolean
  isOptional?: boolean
  isModified?: boolean
  itemCount?: number
  isExpandable?: boolean
  isExpanded?: boolean
  onToggle?: () => void
  onReset?: () => void
  rightActions?: React.ReactNode
}

const FieldHeader: React.FC<FieldHeaderProps> = ({
  name,
  type,
  isArray,
  isOptional,
  isModified,
  itemCount,
  isExpandable,
  isExpanded,
  onToggle,
  onReset,
  rightActions,
}) => (
  <div
    className={`group flex items-center gap-2 ${isExpandable ? '-m-1 cursor-pointer rounded p-1 hover:bg-gray-100' : ''}`}
    onClick={isExpandable ? onToggle : undefined}
  >
    {isExpandable && (
      <ChevronIcon
        isExpanded={isExpanded || false}
        onClick={(e) => {
          e.stopPropagation()
          onToggle?.()
        }}
      />
    )}
    <span className="text-sm font-medium text-gray-700">{name}</span>
    <TypeBadges type={type} isArray={isArray} isOptional={isOptional} isModified={isModified} />
    {itemCount !== undefined && (
      <span className="text-xs text-gray-500">
        ({itemCount} {itemCount === 1 ? 'item' : 'items'})
      </span>
    )}
    <div className="ml-auto flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
      {/* Reset button - only show when modified */}
      {isModified && onReset && (
        <button
          className="rounded p-1 text-red-600 hover:bg-red-100"
          onClick={(e) => {
            e.stopPropagation()
            onReset()
          }}
          title="Reset to original value"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      )}
      {rightActions}
    </div>
  </div>
)

interface FieldContainerProps {
  depth: number
  children: React.ReactNode
}

const FieldContainer: React.FC<FieldContainerProps> = ({ depth, children }) => {
  const isTopLevel = depth === 0
  const paddingLeft = depth * 8 + 16

  return (
    <div
      className={
        (isTopLevel ? 'rounded-lg border border-gray-200 bg-gray-50 p-3' : 'relative py-2') +
        ' group'
      }
      style={{ paddingLeft: isTopLevel ? undefined : `${paddingLeft}px` }}
    >
      {!isTopLevel && (
        <div className="absolute bottom-0 left-0 top-0 flex">
          <div
            className="mr-4 mt-1 w-0.5 bg-gray-300"
            style={{
              height: 'calc(100% - 4px)',
              opacity: 1,
            }}
          />
        </div>
      )}
      {children}
    </div>
  )
}

interface CollapsibleContentProps {
  isExpanded: boolean
  children: React.ReactNode
}

const CollapsibleContent: React.FC<CollapsibleContentProps> = ({ isExpanded, children }) => (
  <AnimatePresence>
    {isExpanded && (
      <motion.div
        className="mt-2"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
)

// =============================================================================
// FIELD TYPE COMPONENTS
// =============================================================================

interface FieldComponentProps {
  field: FrontendFieldInfo
  depth: number
  editMode: boolean
  onValueChange?: (name: string, value: any) => void
  onReset?: (fieldPath: string) => void
  fieldPath?: string
}

const DiscriminatedUnionField: React.FC<FieldComponentProps> = ({
  field,
  depth,
  editMode,
  onValueChange,
  onReset,
  fieldPath = field.name,
}) => {
  const childCount = field.unionVariant?.fields?.length || 0
  const [isExpanded, setIsExpanded] = useState(childCount < 3)

  return (
    <FieldContainer depth={depth}>
      <FieldHeader
        name={field.name}
        type="discriminated-union"
        isModified={field.isModified}
        isExpandable={true}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
        rightActions={null}
      />

      <CollapsibleContent isExpanded={isExpanded}>
        <div className="mb-2 rounded border border-indigo-200 bg-indigo-50 p-2">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-sm font-medium text-indigo-900">{field.discriminatorKey}</span>
            <TypeBadges type="enum" />
          </div>

          {editMode ? (
            <select
              className="w-full rounded border border-indigo-300 bg-white px-2 py-1 text-sm"
              value={field.discriminatorValue || ''}
              onChange={(e) =>
                onValueChange?.(`${fieldPath}.${field.discriminatorKey}`, e.target.value)
              }
            >
              <option value="">Select {field.discriminatorKey}...</option>
              {field.discriminatorOptions?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <ExpandableValue value={field.discriminatorValue} />
          )}
        </div>

        {field.unionVariant && (
          <div className="space-y-1">
            {field.unionVariant.fields
              .filter((variantField) => variantField.name !== field.discriminatorKey) // Filter out discriminator field to avoid duplication
              .map((variantField) => (
                <FieldRenderer
                  key={variantField.name}
                  field={variantField}
                  depth={depth + 1}
                  editMode={editMode}
                  onValueChange={onValueChange}
                  onReset={onReset}
                  fieldPath={`${fieldPath}.${variantField.name}`}
                />
              ))}
          </div>
        )}
      </CollapsibleContent>
    </FieldContainer>
  )
}

const ArrayField: React.FC<FieldComponentProps> = ({
  field,
  depth,
  editMode,
  onValueChange,
  onReset,
  fieldPath = field.name,
}) => {
  const childCount = field.properties?.length || 0
  const [isExpanded, setIsExpanded] = useState(childCount < 3)
  // Only treat as template variable if it's a pure string template variable
  const isVariable = useMemo(
    () => isTemplateToken(field.value) && typeof field.value === 'string',
    [field.value]
  )

  return (
    <FieldContainer depth={depth}>
      <FieldHeader
        name={field.name}
        type={field.arrayElementType || 'unknown'}
        isArray={true}
        isModified={field.isModified}
        itemCount={field.properties?.length}
        isExpandable={true}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
        onReset={() => onReset?.(fieldPath)}
        rightActions={
          editMode ? (
            <div className="flex items-center gap-1">
              {field.isOptional && (
                <button
                  className={`rounded px-2 py-0.5 text-xs ${
                    field.value === null
                      ? 'border border-red-300 bg-red-50 text-red-700'
                      : 'border border-gray-300 bg-white text-gray-600'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onValueChange?.(fieldPath, null)
                  }}
                  title="Set to null"
                >
                  {field.value === null ? 'Null' : 'Set null'}
                </button>
              )}
              <button
                className={`rounded px-2 py-0.5 text-xs ${
                  isVariable
                    ? 'border border-amber-300 bg-amber-50 text-amber-700'
                    : 'border border-gray-300 bg-white text-gray-600'
                }`}
                onClick={(e) => {
                  e.stopPropagation()
                  if (isVariable) {
                    // Switching from variable to literal - provide appropriate default
                    const defaultValue = getDefaultValueForType('array')
                    onValueChange?.(fieldPath, defaultValue)
                  } else {
                    // Switching from literal to variable
                    onValueChange?.(fieldPath, '{{variable}}')
                  }
                }}
                title={isVariable ? 'Use literal' : 'Use variable'}
              >
                {isVariable ? 'Variable' : 'Use variable'}
              </button>
            </div>
          ) : null
        }
      />

      {editMode && isVariable ? (
        <VariableTokenInput value={field.value} onChange={(v) => onValueChange?.(fieldPath, v)} />
      ) : (
        <CollapsibleContent isExpanded={isExpanded}>
          <div className="space-y-1">
            {field.properties?.map((item, index) => (
              <FieldRenderer
                key={item.name}
                field={{
                  ...item,
                  name: `Item ${index}`,
                  // Preserve enum values from parent array field
                  enumValues:
                    item.enumValues ||
                    (field.arrayElementType === 'enum' ? field.enumValues : undefined),
                }}
                depth={depth + 1}
                editMode={editMode}
                onValueChange={onValueChange}
                onReset={onReset}
                fieldPath={`${fieldPath}[${index}]`}
              />
            ))}
          </div>
        </CollapsibleContent>
      )}
    </FieldContainer>
  )
}

const ObjectField: React.FC<FieldComponentProps> = ({
  field,
  depth,
  editMode,
  onValueChange,
  onReset,
  fieldPath = field.name,
}) => {
  const childCount = field.properties?.length || 0
  const [isExpanded, setIsExpanded] = useState(childCount < 3)
  // Only treat as template variable if it's a pure string template variable
  const isVariable = useMemo(
    () => isTemplateToken(field.value) && typeof field.value === 'string',
    [field.value]
  )

  return (
    <FieldContainer depth={depth}>
      <FieldHeader
        name={field.name}
        type="object"
        isModified={field.isModified}
        itemCount={field.properties?.length}
        isExpandable={true}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
        onReset={() => onReset?.(fieldPath)}
        rightActions={
          editMode ? (
            <div className="flex items-center gap-1">
              {field.isOptional && (
                <button
                  className={`rounded px-2 py-0.5 text-xs ${
                    field.value === null
                      ? 'border border-red-300 bg-red-50 text-red-700'
                      : 'border border-gray-300 bg-white text-gray-600'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onValueChange?.(fieldPath, null)
                  }}
                  title="Set to null"
                >
                  {field.value === null ? 'Null' : 'Set null'}
                </button>
              )}
              <button
                className={`rounded px-2 py-0.5 text-xs ${
                  isVariable
                    ? 'border border-amber-300 bg-amber-50 text-amber-700'
                    : 'border border-gray-300 bg-white text-gray-600'
                }`}
                onClick={(e) => {
                  e.stopPropagation()
                  if (isVariable) {
                    // Switching from variable to literal - provide appropriate default
                    const defaultValue = getDefaultValueForType('object')
                    onValueChange?.(fieldPath, defaultValue)
                  } else {
                    // Switching from literal to variable
                    onValueChange?.(fieldPath, '{{variable}}')
                  }
                }}
                title={isVariable ? 'Use literal' : 'Use variable'}
              >
                {isVariable ? 'Variable' : 'Use variable'}
              </button>
            </div>
          ) : null
        }
      />

      {editMode && isVariable ? (
        <VariableTokenInput value={field.value} onChange={(v) => onValueChange?.(fieldPath, v)} />
      ) : (
        <CollapsibleContent isExpanded={isExpanded}>
          <div className="space-y-1">
            {field.properties?.map((prop) => (
              <FieldRenderer
                key={prop.name}
                field={prop}
                depth={depth + 1}
                editMode={editMode}
                onValueChange={onValueChange}
                onReset={onReset}
                fieldPath={`${fieldPath}.${prop.name}`}
              />
            ))}
          </div>
        </CollapsibleContent>
      )}
    </FieldContainer>
  )
}

const EnumField: React.FC<FieldComponentProps> = ({
  field,
  depth,
  editMode,
  onValueChange,
  onReset,
  fieldPath = field.name,
}) => {
  const isVariable = useMemo(() => isTemplateToken(field.value), [field.value])

  // Ensure the field value is preserved when switching to edit mode
  const currentValue = field.value !== undefined && field.value !== null ? field.value : ''

  return (
    <FieldContainer depth={depth}>
      <FieldHeader
        name={field.name}
        type="enum"
        isOptional={field.isOptional}
        isModified={field.isModified}
        onReset={() => onReset?.(fieldPath)}
        rightActions={
          editMode ? (
            <div className="flex items-center gap-1">
              {field.isOptional && (
                <button
                  className={`rounded px-2 py-0.5 text-xs ${
                    field.value === null
                      ? 'border border-red-300 bg-red-50 text-red-700'
                      : 'border border-gray-300 bg-white text-gray-600'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onValueChange?.(fieldPath, null)
                  }}
                  title="Set to null"
                >
                  {field.value === null ? 'Null' : 'Set null'}
                </button>
              )}
              <button
                className={`rounded px-2 py-0.5 text-xs ${
                  isVariable
                    ? 'border border-amber-300 bg-amber-50 text-amber-700'
                    : 'border border-gray-300 bg-white text-gray-600'
                }`}
                onClick={(e) => {
                  e.stopPropagation()
                  if (isVariable) {
                    // Switching from variable to literal - preserve current value if it's a valid enum option
                    let defaultValue = field.value
                    if (!field.enumValues?.includes(defaultValue)) {
                      // If current value is not a valid enum option, use first enum option or empty string
                      defaultValue = field.enumValues?.[0] || ''
                    }
                    onValueChange?.(fieldPath, defaultValue)
                  } else {
                    // Switching from literal to variable
                    onValueChange?.(fieldPath, '{{variable}}')
                  }
                }}
                title={isVariable ? 'Use literal' : 'Use variable'}
              >
                {isVariable ? 'Variable' : 'Use variable'}
              </button>
            </div>
          ) : null
        }
      />

      {editMode ? (
        isVariable ? (
          <VariableTokenInput value={field.value} onChange={(v) => onValueChange?.(fieldPath, v)} />
        ) : (
          <select
            className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
            value={field.value === null ? 'null' : currentValue}
            onChange={(e) => {
              if (e.target.value === 'null') {
                onValueChange?.(fieldPath, null)
              } else {
                onValueChange?.(fieldPath, e.target.value || undefined)
              }
            }}
          >
            <option value="">Select {field.name}...</option>
            {field.isOptional && <option value="null">null</option>}
            {field.enumValues?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )
      ) : (
        <ExpandableValue value={field.value} />
      )}
    </FieldContainer>
  )
}

const PrimitiveField: React.FC<FieldComponentProps> = ({
  field,
  depth,
  editMode,
  onValueChange,
  onReset,
  fieldPath = field.name,
}) => {
  const supportsVariable = field.type !== 'string' && field.type !== 'unknown'
  const isVariable = useMemo(() => isTemplateToken(field.value), [field.value])

  return (
    <FieldContainer depth={depth}>
      <FieldHeader
        name={field.name}
        type={field.type}
        isArray={field.isArray}
        isOptional={field.isOptional}
        isModified={field.isModified}
        onReset={() => onReset?.(fieldPath)}
        rightActions={
          editMode ? (
            <div className="flex items-center gap-1">
              {field.isOptional && (
                <button
                  className={`rounded px-2 py-0.5 text-xs ${
                    field.value === null
                      ? 'border border-red-300 bg-red-50 text-red-700'
                      : 'border border-gray-300 bg-white text-gray-600'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onValueChange?.(fieldPath, null)
                  }}
                  title="Set to null"
                >
                  {field.value === null ? 'Null' : 'Set null'}
                </button>
              )}
              {supportsVariable && (
                <button
                  className={`rounded px-2 py-0.5 text-xs ${
                    isVariable
                      ? 'border border-amber-300 bg-amber-50 text-amber-700'
                      : 'border border-gray-300 bg-white text-gray-600'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (isVariable) {
                      // Switching from variable to literal - provide appropriate default
                      const defaultValue = getDefaultValueForType(field.type)
                      onValueChange?.(fieldPath, defaultValue)
                    } else {
                      // Switching from literal to variable
                      onValueChange?.(fieldPath, '{{variable}}')
                    }
                  }}
                  title={isVariable ? 'Use literal' : 'Use variable'}
                >
                  {isVariable ? 'Variable' : 'Use variable'}
                </button>
              )}
            </div>
          ) : null
        }
      />

      {editMode ? (
        <div>
          {/* Inline toggle removed; replaced by hover action in header */}
          {isVariable ? (
            <VariableTokenInput
              value={field.value}
              onChange={(value) => onValueChange?.(fieldPath, value)}
            />
          ) : (
            <PrimitiveInput
              type={field.type}
              value={field.value}
              onChange={(value) => onValueChange?.(fieldPath, value)}
            />
          )}
        </div>
      ) : (
        <ExpandableValue value={field.value} />
      )}
    </FieldContainer>
  )
}

// =============================================================================
// MAIN FIELD RENDERER
// =============================================================================

interface FieldRendererProps {
  field: FrontendFieldInfo
  depth?: number
  editMode?: boolean
  onValueChange?: (name: string, value: any) => void
  onReset?: (fieldPath: string) => void
  fieldPath?: string
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  depth = 0,
  editMode = false,
  onValueChange,
  onReset,
  fieldPath = field.name,
}) => {
  // CRITICAL FIX: Safety net to prevent object regression
  // If an object field lost its properties but has object data, reconstruct properties
  const safeField = useMemo(() => {
    if (
      field.type === 'object' &&
      !field.properties &&
      field.value &&
      typeof field.value === 'object' &&
      !Array.isArray(field.value) &&
      field.value !== null
    ) {
      console.warn('[FieldRenderer] Object field missing properties, reconstructing:', field.name)
      return {
        ...field,
        properties: Object.entries(field.value).map(([key, value]) => ({
          name: key,
          type: Array.isArray(value)
            ? 'array'
            : value === null
              ? 'null'
              : (typeof value as FrontendDataType),
          value,
          isOptional: false,
          isArray: Array.isArray(value),
          properties: Array.isArray(value)
            ? value.map((item, index) => ({
                name: `${index}`,
                type: typeof item as FrontendDataType,
                value: item,
                isOptional: false,
                isArray: false,
              }))
            : typeof value === 'object' && value !== null
              ? Object.entries(value).map(([subKey, subValue]) => ({
                  name: subKey,
                  type: typeof subValue as FrontendDataType,
                  value: subValue,
                  isOptional: false,
                  isArray: false,
                }))
              : undefined,
        })),
      }
    }
    return field
  }, [field])

  const fieldProps = { field: safeField, depth, editMode, onValueChange, onReset, fieldPath }

  if (safeField.type === 'discriminated-union') {
    return <DiscriminatedUnionField {...fieldProps} />
  }

  if (safeField.isArray && safeField.properties) {
    return <ArrayField {...fieldProps} />
  }

  if (safeField.type === 'object' && safeField.properties) {
    return <ObjectField {...fieldProps} />
  }

  if (safeField.type === 'enum') {
    return <EnumField {...fieldProps} />
  }

  return <PrimitiveField {...fieldProps} />
}

// =============================================================================
// MAIN SECTION COMPONENT
// =============================================================================

interface NodeDataSectionProps {
  title: string
  fields?: FrontendFieldInfo[]
  schema?: z.ZodTypeAny
  data?: Record<string, any>
  originalData?: Record<string, any>
  allowVariables?: boolean
  editMode?: boolean
  onValueChange?: (fieldName: string, value: any) => void
  onReset?: (fieldPath: string) => void
  nodeDataSectionType?: 'io' | 'error'
}

const NodeDataSectionClient: React.FC<NodeDataSectionProps> = ({
  title,
  fields,
  schema,
  data,
  originalData,
  allowVariables = true,
  editMode = false,
  onValueChange,
  onReset,
  nodeDataSectionType = 'io',
}) => {
  // Generate fields from schema if not provided
  const resolvedFields = useMemo(() => {
    if (fields) {
      return fields
    }

    if (schema && data !== undefined) {
      try {
        return createFieldsFromSchema(schema, data, allowVariables, originalData)
      } catch (error) {
        console.error('Error creating fields from schema:', error)
        return []
      }
    }

    return []
  }, [fields, schema, data, allowVariables, originalData, title])

  if (!resolvedFields || !resolvedFields.length) return null

  return (
    <div>
      <h4
        className={`mb-3 text-sm font-medium ${
          nodeDataSectionType === 'error' ? 'rounded bg-red-500 p-2 text-white' : 'text-gray-900'
        }`}
      >
        {title}
      </h4>
      <div className="space-y-2">
        {resolvedFields.map((field, index) => (
          <FieldRenderer
            key={field.name || `field-${index}`}
            field={field}
            editMode={editMode}
            onValueChange={onValueChange}
            onReset={onReset}
            fieldPath={field.name}
          />
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// SCHEMA-DRIVEN COMPONENT
// =============================================================================

interface SchemaDataDisplayProps {
  title: string
  schema: z.ZodTypeAny
  data: Record<string, any>
  originalData?: Record<string, any>
  allowVariables?: boolean
  editMode?: boolean
  onValueChange?: (fieldName: string, value: any) => void
  onReset?: (fieldPath: string) => void
  nodeDataSectionType?: 'io' | 'error'
}

/**
 * A schema-driven version of NodeDataSection that automatically generates
 * field configuration from zod schemas with variable support
 */
const SchemaDataDisplayClient: React.FC<SchemaDataDisplayProps> = ({
  title,
  schema,
  data,
  originalData,
  allowVariables = true,
  editMode = false,
  onValueChange,
  onReset,
  nodeDataSectionType = 'io',
}) => {
  return (
    <NodeDataSectionClient
      title={title}
      schema={schema}
      data={data}
      originalData={originalData}
      allowVariables={allowVariables}
      editMode={editMode}
      onValueChange={onValueChange}
      onReset={onReset}
      nodeDataSectionType={nodeDataSectionType}
    />
  )
}

export const NodeDataSection = NodeDataSectionClient
export const SchemaDataDisplay = SchemaDataDisplayClient
